import { IconButton } from '@festina-life/ui/actions'
import { FullscreenSpinner } from '@festina-life/ui/feedback'
import { Activity, useMemo } from 'react'
import {
  Group,
  Panel,
  Separator,
  useDefaultLayout,
} from 'react-resizable-panels'
import { useLocation, useNavigate } from 'react-router'

import { useEnumId } from '../../hooks/useEnumId'
import { enumDefinitionPath, enumLiteralPath } from '../../system/paths'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import { EnumDefinitionDetails } from './EnumDefinitionDetails'
import { EnumLiteralDetails } from './EnumLiteralDetails'
import { EnumTable } from './EnumTable'
import { EnumViewerTopBar } from './EnumViewerTopBar'

export const EnumViewer = () => {
  const navigate = useNavigate()
  const enumId = useEnumId()
  const { isLoading } = useOperationsContext()
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const { hash: viewingPath, search, pathname } = useLocation()
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'enumViewPanels',
    storage: localStorage,
  })
  const configuredEnumForId = applicationEnumDefinitions.get(enumId)
  const [viewingEnumName, viewingLiteralName] = viewingPath
    .substring(1)
    .split('.')
  const isViewingEnumDefinition =
    !viewingLiteralName && viewingEnumName === enumId

  const viewingLiteral = useMemo(() => {
    if (
      !configuredEnumForId ||
      viewingEnumName !== enumId ||
      !viewingLiteralName
    ) {
      return null
    }

    return configuredEnumForId.literals.find((literal) =>
      literal.type === 'modeled'
        ? literal.literalName === viewingLiteralName
        : literal.internalOperation.entity?.name === viewingLiteralName,
    )!
  }, [configuredEnumForId, enumId, viewingEnumName, viewingLiteralName])

  const isViewingDetails = isViewingEnumDefinition || viewingLiteral

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  if (!configuredEnumForId) {
    // Nonexistent elements will be navigated away from by the useCloseNonExistentLinks hook
    return null
  }

  const handleNavigateToLiteral = (literalName: string) =>
    navigate(
      `${pathname}${search}#${enumLiteralPath({ enumName: enumId, literalName })}`,
    )

  const handleNavigateToEnumDefinition = () =>
    navigate(`${pathname}${search}#${enumDefinitionPath(enumId)}`)

  const handleCloseDetails = () => navigate(`${pathname}${search}`)

  return (
    <div className="grid size-full grid-rows-[auto_1fr] overflow-hidden">
      <EnumViewerTopBar />
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        <Panel className="size-full overflow-hidden" id="enumMainView">
          <div className="relative h-full overflow-y-auto px-4 pb-40">
            <EnumTable
              onViewEnumDetails={handleNavigateToEnumDefinition}
              onViewLiteralDetails={handleNavigateToLiteral}
              value={configuredEnumForId}
              selection={
                isViewingEnumDefinition
                  ? { variant: 'definition' }
                  : { variant: 'literal', value: viewingLiteralName }
              }
            />
          </div>
        </Panel>
        <Separator className="w-1 dark:bg-slate-700 bg-slate-300" />
        <Activity mode={isViewingDetails ? 'visible' : 'hidden'}>
          <Panel
            id="enumDetailsPanel"
            className="size-full max-h-full overflow-hidden flex flex-col"
            minSize="5%"
            defaultSize="25%"
          >
            <IconButton
              className="absolute! right-2 top-2"
              onClick={handleCloseDetails}
              iconSize="md"
              iconName="XCircle"
            />

            {isViewingEnumDefinition && (
              <EnumDefinitionDetails
                value={configuredEnumForId}
                onClose={handleCloseDetails}
              />
            )}
            {!!viewingLiteral && (
              <EnumLiteralDetails
                key={
                  viewingLiteral?.type === 'modeled'
                    ? viewingLiteral.literalName
                    : viewingLiteral?.internalOperation.entity?.name
                }
                value={viewingLiteral}
                onClose={handleCloseDetails}
              />
            )}
          </Panel>
        </Activity>
      </Group>
    </div>
  )
}
